/**
 * kartoteka-list-columns.ts — kolumny list kartotek SYSTEMOWYCH, które lista klienta rysuje
 * POZA katalogiem pól (id kolumny ≠ slug pola): Firma (`company`), Kategoria (`category` /
 * na Towarach `record_category`), szczegóły umowy, Nadawca pisma, kolumny procesu, kolumny
 * syntetyczne pracowników/użytkowników/kontaktów.
 *
 * Jedno źródło dla:
 *   - kreatora listy klienta — te id są dozwolone (zapis ich nie wycina) i są w palecie
 *     „Dodaj kolumnę",
 *   - (dalej) kreatora listy w panelu ITE (menu modułu) — te same kolumny pod tymi samymi id.
 *
 * Etykiety to klucze i18n panelu (apps/admin). Strona listy, która dokłada kolumnę spoza
 * katalogu, MUSI mieć tu wpis — inaczej kreator listy klienta wytnie ją przy zapisie.
 */

export interface KartotekaExtraListColumn {
  /** Id kolumny na liście klienta (stałe — zapisane układy dopasowują po nim). */
  id: string;
  labelI18nKey: string;
}

const COMPANY: KartotekaExtraListColumn = {
  id: 'company',
  labelI18nKey: 'tenant.documents.columns.company',
};
const CATEGORY: KartotekaExtraListColumn = {
  id: 'category',
  labelI18nKey: 'tenant.recordCategories.picker.label',
};

/** Kolumny procesu (aktywny proces + bieżący etap) — wspólne dla 7 klas dokumentów. */
function processColumns(prefix: string): KartotekaExtraListColumn[] {
  return [
    { id: 'active_process_name', labelI18nKey: `${prefix}.columns.activeProcessName` },
    { id: 'current_stage', labelI18nKey: `${prefix}.columns.currentStage` },
  ];
}

export const KARTOTEKA_EXTRA_LIST_COLUMNS: Readonly<
  Record<string, readonly KartotekaExtraListColumn[]>
> = {
  contractors: [COMPANY, CATEGORY],
  employees: [
    { id: 'company', labelI18nKey: 'tenant.employees.field.company' },
    { id: 'account', labelI18nKey: 'tenant.employees.col.account' },
    // Syntetyczny drugi id na ten sam slug backendu `last_name` co „Imię i nazwisko".
    { id: 'surname', labelI18nKey: 'tenant.employees.col.surname' },
    CATEGORY,
  ],
  users: [{ id: 'companies', labelI18nKey: 'tenant.users.col.companies' }, CATEGORY],
  contractor_contacts: [
    { id: 'name', labelI18nKey: 'tenant.contractorContacts.col.name' },
    { id: 'contractor', labelI18nKey: 'tenant.contractorContacts.col.contractor' },
    { id: 'position', labelI18nKey: 'tenant.contractorContacts.col.position' },
    { id: 'email', labelI18nKey: 'tenant.contractorContacts.col.email' },
    { id: 'phone', labelI18nKey: 'tenant.contractorContacts.col.phone' },
  ],
  products: [COMPANY, { id: 'record_category', labelI18nKey: CATEGORY.labelI18nKey }],
  purchase_invoices: [COMPANY, CATEGORY, ...processColumns('tenant.purchaseInvoices')],
  sale_invoices: [COMPANY, CATEGORY, ...processColumns('tenant.saleInvoices')],
  contracts: [
    COMPANY,
    CATEGORY,
    {
      id: 'contract_number',
      labelI18nKey: 'tenant.documents.contracts.columns.contractNumber',
    },
    {
      id: 'contract_status',
      labelI18nKey: 'tenant.documents.contracts.columns.contractStatus',
    },
    { id: 'period', labelI18nKey: 'tenant.documents.contracts.columns.period' },
    { id: 'contract_value', labelI18nKey: 'tenant.documents.contracts.columns.contractValue' },
    { id: 'counterparty', labelI18nKey: 'tenant.documents.contracts.columns.counterparty' },
    ...processColumns('tenant.documents.contracts'),
  ],
  letters: [
    COMPANY,
    CATEGORY,
    { id: 'sender', labelI18nKey: 'tenant.documents.letters.columns.sender' },
    ...processColumns('tenant.documents.letters'),
  ],
  commercial: [COMPANY, CATEGORY, ...processColumns('tenant.documents.commercial')],
  internal: [COMPANY, CATEGORY, ...processColumns('tenant.documents.internal')],
  // 7. klasa dokumentów (wnioski specjalne, m.in. urlopowe) — slug kartoteki to `specjalne`,
  // etykiety kolumn procesu pod `tenant.documents.special`.
  specjalne: [COMPANY, CATEGORY, ...processColumns('tenant.documents.special')],
};

/** Kolumny spoza katalogu dla kartoteki (pusta lista = brak). */
export function kartotekaExtraListColumns(slug: string): readonly KartotekaExtraListColumn[] {
  return KARTOTEKA_EXTRA_LIST_COLUMNS[slug] ?? [];
}

/**
 * Pola katalogu, które lista klienta podpisuje INACZEJ niż katalog: kartoteka → slug pola → klucz
 * i18n panelu. Kreator listy (zakładki „Kolumny" i „Kalendarz") podpisuje wtedy pole tą nazwą —
 * tak jak nagłówek kolumny na liście — zamiast nazwy z katalogu.
 *
 * Dziś jedyny wpis: pracownicy. Kolumna o id `last_name` jest na liście złożoną „Imię i nazwisko"
 * (nadpisanie w `Employees.tsx`), a samo nazwisko rysuje osobna kolumna `surname` (wpis wyżej).
 * Bez nadpisania kreator podpisałby obie „Nazwisko".
 */
export const KARTOTEKA_LIST_FIELD_LABEL_KEYS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  employees: { last_name: 'tenant.employees.col.name' },
};

/** Klucz i18n nazwy pola katalogu na liście klienta; `undefined` = nazwa z katalogu bez zmian. */
export function kartotekaListFieldLabelKey(slug: string, fieldSlug: string): string | undefined {
  if (!Object.hasOwn(KARTOTEKA_LIST_FIELD_LABEL_KEYS, slug)) return undefined;
  const keys = KARTOTEKA_LIST_FIELD_LABEL_KEYS[slug];
  return keys !== undefined && Object.hasOwn(keys, fieldSlug) ? keys[fieldSlug] : undefined;
}

/**
 * Pola katalogu umów z satelity `document_contract_details` (ADR-0109b). Lista umów klienta NIE
 * rysuje ich pod tymi slugami — ma własne kolumny spoza katalogu (`contract_number`,
 * `contract_status`, `period`, `contract_value`, `counterparty`; trzy pierwsze mają to samo id co
 * pole, ale to kolumna strony, nie pole katalogu).
 */
const CONTRACT_DETAIL_FIELD_SLUGS: readonly string[] = [
  'contract_number',
  'contract_number_missing',
  'contract_status',
  'subject_matter',
  'subject_matter_confidential',
  'period_from',
  'period_to',
  'period_indefinite',
  'period_duration_value',
  'period_duration_unit',
  'contract_value',
  'value_confidential',
  'contract_value_description',
];

/**
 * Pola katalogu, których lista klienta NIE rysuje pod ich slugiem:
 *  - pola z własną kolumną spoza katalogu: `company_id` → kolumna Firma (`company` / `companies`),
 *    `category_id` → Kategoria (`category` / `record_category`) — `replacementColumnId` zna ich
 *    kolumnę zastępczą;
 *  - umowy: 13 pól umowy z satelity (ADR-0109b) — lista rysuje te dane własnymi kolumnami umowy,
 *    a samych pól katalogu nie pokazuje (bez kolumny zastępczej per pole).
 * Kreator listy w panelu ITE ich nie oferuje (inaczej kolumna z ITE nie pojawiłaby się u klienta)
 * — decyzja 2026-10-05: „ITE oferuje dokładnie kolumny klienta".
 */
export function kartotekaExcludedListFieldSlugs(slug: string): readonly string[] {
  const ids = new Set(kartotekaExtraListColumns(slug).map((c) => c.id));
  const out: string[] = [];
  if (ids.has('company') || ids.has('companies')) out.push('company_id');
  if (ids.has('category') || ids.has('record_category')) out.push('category_id');
  if (slug === 'contracts') out.push(...CONTRACT_DETAIL_FIELD_SLUGS);
  return out;
}

/**
 * Kolumna listy klienta, która zastępuje wykluczone pole katalogu (np. `company_id` → `company`);
 * `null` = pole nie jest zastępowane (inne pole albo kartoteka bez takiej kolumny). Reguła
 * `normalizeKartotekaListConfig` — wołana też przez zakładkę „Kalendarz" kreatora listy, żeby
 * zapisane pole katalogu (`company_id`) pokazać jako kolumnę klienta (`company`).
 */
export function replacementColumnId(slug: string, fieldSlug: string): string | null {
  const ids = new Set(kartotekaExtraListColumns(slug).map((c) => c.id));
  const candidates =
    fieldSlug === 'company_id'
      ? ['company', 'companies']
      : fieldSlug === 'category_id'
        ? ['category', 'record_category']
        : [];
  return candidates.find((c) => ids.has(c)) ?? null;
}

/**
 * Przepisuje stary układ listy kartoteki systemowej na kolumny klienta: wykluczone pole
 * (`company_id`, `category_id`) → jego kolumna zastępcza (Firma, Kategoria), duplikaty usunięte;
 * to samo dla domyślnego sortowania. Układy zapisane, zanim paleta ITE przestała oferować te
 * pola, dalej dają się zapisać i trafiają do klienta z widoczną kolumną.
 */
export function normalizeKartotekaListConfig<
  T extends { columns: Array<{ id: string }>; defaultSort?: { column: string } | null },
>(slug: string, config: T): T {
  if (!config || !Array.isArray(config.columns)) return config;
  const map = (id: string) => replacementColumnId(slug, id) ?? id;
  const seen = new Set<string>();
  const columns = config.columns
    .map((c) => ({ ...c, id: map(c.id) }))
    .filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  const defaultSort = config.defaultSort
    ? { ...config.defaultSort, column: map(config.defaultSort.column) }
    : config.defaultSort;
  return { ...config, columns, ...(config.defaultSort !== undefined ? { defaultSort } : {}) };
}

/** Id kolumn listy kartoteki systemowej: pola katalogu − wykluczone + kolumny spoza katalogu. */
export function kartotekaListColumnIds(slug: string, fieldSlugs: Iterable<string>): Set<string> {
  const excluded = new Set(kartotekaExcludedListFieldSlugs(slug));
  const out = new Set<string>();
  for (const f of fieldSlugs) if (!excluded.has(f)) out.add(f);
  for (const c of kartotekaExtraListColumns(slug)) out.add(c.id);
  return out;
}
